import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatMenuModule } from '@angular/material/menu';
import { MatDialog } from '@angular/material/dialog';
import { MainLayoutComponent } from '../../../../_layouts/main-layout/main-layout.component';
import { AlertService } from '../../../../_services/alert.service';
import { UserService } from '../../../../_services/users.service';
import { ConfirmDialogComponent } from '../../../../../Shared/_components/confirm-dialog/confirm-dialog.component';
import { PurchaseShipmentService } from '../../../../_services/purchase-shipment.service';
import { ShipmentContainer, ShipmentLine, ShipmentOrderSummary } from '../../../../_interfaces/purchase-shipment';
import { PORTS_OF_DISCHARGE, PORTS_OF_LOADING } from '../../ports';
import { CURRENCY_OPTIONS, currencySymbol } from '../../currencies';
import { SHIPMENTS_LIST_KEY, lastListQuery } from '../../list-state';
import { SHIPMENT_STATUSES, shipmentStatusClass, shipmentStatusSoft } from '../shipment-status';
import { ProductService, ProductPickerItem } from '../../../../_services/product.service';
import { ProductPickerComponent } from '../../../../../Shared/_components/product-picker/product-picker.component';

@Component({
    selector: 'app-create-shipment',
    imports: [
        MainLayoutComponent,
        MatIconModule,
        MatTooltipModule,
        MatProgressSpinnerModule,
        MatAutocompleteModule,
        MatMenuModule,
        FormsModule,
        CommonModule,
        DatePipe,
        RouterLink,
        ProductPickerComponent,
    ],
    templateUrl: './create-shipment.component.html',
    // Form styles shared with the purchase order page.
    styleUrls: ['../../purchase-form.css', './create-shipment.component.css']
})
export class CreateShipmentComponent implements OnInit {
  isEditMode = false;
  shipmentId: number | null = null;
  loading = false;
  saving = false;

  reference = '';
  invoiceNumber = '';
  invoiceDate = '';
  containerNo = '';
  billOfLading = '';
  etd = '';
  eta = '';
  portOfLoading = '';
  portOfDischarge = '';
  vesselDetails = '';
  shippingLine = '';
  status = 'BOOKED';
  store = 'Markstey';
  notes = '';

  readonly statusOptions = SHIPMENT_STATUSES;
  readonly storeOptions = ['Kettering', 'Markstey'];
  readonly loadingPorts = PORTS_OF_LOADING;
  readonly dischargePorts = PORTS_OF_DISCHARGE;
  readonly unitOptions = ['PCS', 'M2', 'M', 'PACK', 'BOX', 'CRATE', 'PALLET', 'SET', 'KG', 'TONNE'];
  readonly incotermOptions = ['EXW', 'FCA', 'FAS', 'FOB', 'CFR', 'CIF', 'CPT', 'CIP', 'DPU', 'DAP', 'DDP'];
  // Same currencies as purchase orders.
  readonly currencyOptions = CURRENCY_OPTIONS;
  getStatusClass = shipmentStatusClass;
  currencySymbol = currencySymbol;

  lines: ShipmentLine[] = [];
  // Any number of containers; every line sits in one of them.
  containers: ShipmentContainer[] = [];
  // Where the "add from a purchase order" box drops lines.
  targetContainerKey: string | null = null;
  private nextContainerKey = 1;
  // POs that can go on a shipment (ordered, not fully received), plus any
  // already used on this one.
  availableOrders: ShipmentOrderSummary[] = [];
  private linkedOrders: ShipmentOrderSummary[] = [];
  orderToAdd: number | null = null;
  addingLines = false;
  // Text in the "add from a purchase order" search box.
  orderSearch = '';
  // For the product search on hand-added lines.
  products: ProductPickerItem[] = [];
  // Each purchase order's lines (outstanding qty, price, incoterm...), so a
  // line's product can be picked from its PO and fill in the rest, Odoo-style.
  poLines: Record<number, ShipmentLine[]> = {};
  private poLinesLoading = new Set<number>();

  // The happy path a shipment moves along; CANCELLED sits outside it.
  readonly statusFlow = [
    { value: 'BOOKED', label: 'Booked', icon: 'event_available' },
    { value: 'IN TRANSIT', label: 'In transit', icon: 'directions_boat' },
    { value: 'ARRIVED', label: 'Arrived', icon: 'anchor' },
    { value: 'CLEARED', label: 'Cleared', icon: 'verified' },
    { value: 'DELIVERED', label: 'Delivered', icon: 'warehouse' },
    // Only set by the yard validating a container (Receipts), never by hand.
    { value: 'RECEIVED', label: 'Received', icon: 'inventory' },
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private alertService: AlertService,
    private shipmentService: PurchaseShipmentService,
    private productService: ProductService,
    private userService: UserService,
    private dialog: MatDialog,
  ) {}

  // Deleting is for super admins only (the backend checks too).
  readonly canDelete = (this.userService.isLoggedIn().userrole ?? '')
    .split(',')
    .map((r: string) => r.trim())
    .includes('S-ADMIN');

  confirmDelete(): void {
    if (!this.shipmentId) return;
    const id = this.shipmentId;
    this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          title: `Delete ${this.reference}?`,
          message: `This removes the shipment with its ${this.lines.length} line${this.lines.length === 1 ? '' : 's'} and ${this.containers.length} container${this.containers.length === 1 ? '' : 's'}. It can't be undone.`,
          detail: 'The purchase orders and their stock are not affected.',
          confirmText: 'Delete shipment',
          tone: 'danger',
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (!result?.confirmation) return;
        this.shipmentService.deleteShipment(id).subscribe({
          next: () => {
            this.alertService.alert(`${this.reference} deleted`, 'success');
            this.backToList();
          },
          error: (error) => this.alertService.alert(error?.error?.message || 'Could not delete shipment', 'error'),
        });
      });
  }

  ngOnInit(): void {
    this.shipmentService.getAvailableOrders().subscribe({
      next: (orders) => (this.availableOrders = orders),
      error: () => this.alertService.alert('Could not load purchase orders', 'error'),
    });
    this.productService.getProductPicker().subscribe({
      next: (products) => (this.products = products),
      // Non-fatal: hand-added lines can still be typed as free text.
      error: () => (this.products = []),
    });
    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam) {
      this.isEditMode = true;
      this.shipmentId = Number(idParam);
      this.load(this.shipmentId);
    } else {
      this.addContainer();
    }
  }

  private load(id: number): void {
    this.loading = true;
    this.shipmentService.getShipmentById(id).subscribe({
      next: (s) => {
        this.reference = s.reference;
        this.invoiceNumber = s.invoiceNumber || '';
        this.invoiceDate = s.invoiceDate ? String(s.invoiceDate).slice(0, 10) : '';
        this.containerNo = s.containerNo || '';
        this.billOfLading = s.billOfLading || '';
        this.etd = s.etd ? String(s.etd).slice(0, 10) : '';
        this.eta = s.eta ? String(s.eta).slice(0, 10) : '';
        this.portOfLoading = s.portOfLoading || '';
        this.portOfDischarge = s.portOfDischarge || '';
        this.vesselDetails = s.vesselDetails || '';
        this.shippingLine = s.shippingLine || '';
        this.status = s.status;
        this.store = s.store;
        this.notes = s.notes || '';
        // Saved containers get form keys; lines follow them by containerId.
        this.containers = (s.containers ?? []).map((c) => ({
          id: c.id,
          key: 'c' + c.id,
          containerNo: c.containerNo,
          containerType: c.containerType ?? null,
          sealNo: c.sealNo ?? null,
          // Saved before containers had their own state: follow the shipment.
          status: c.status || s.status,
          clearedAt: c.clearedAt ? String(c.clearedAt).slice(0, 10) : null,
        }));
        if (!this.containers.length) {
          // Saved before containers existed: one container with the old number.
          this.addContainer();
          this.containers[0].containerNo = s.containerNo;
        }
        const keys = new Set(this.containers.map((c) => c.key));
        this.lines = s.items.map((i) => {
          const key = i.containerId ? 'c' + i.containerId : null;
          return { ...i, containerKey: key && keys.has(key) ? key : this.containers.length === 1 ? this.containers[0].key : null };
        });
        this.targetContainerKey = this.containers[0]?.key ?? null;
        this.linkedOrders = s.orders;
        s.orders.forEach((o) => this.ensurePoLines(o.id));
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.alertService.alert('Shipment not found', 'error');
        this.backToList();
      },
    });
  }

  get orderChoices(): ShipmentOrderSummary[] {
    const ids = new Set(this.availableOrders.map((o) => o.id));
    return [...this.availableOrders, ...this.linkedOrders.filter((o) => !ids.has(o.id))];
  }

  orderReference(id: number | null): string {
    return this.orderChoices.find((o) => o.id === id)?.reference ?? (id ? `PO ${id}` : '');
  }

  // Keeps a saved port that isn't in the list selectable.
  portChoices(list: string[], current: string): string[] {
    return current && !list.includes(current) ? [...list, current] : list;
  }

  // Drops a purchase order's outstanding lines into the chosen container (in
  // its currency / incoterm), skipping any product of that PO already in
  // that container. The same product can still go in other containers.
  addOrderLines(): void {
    if (!this.orderToAdd) return;
    const poId = this.orderToAdd;
    const containerKey = this.targetContainerKey ?? this.containers[0]?.key ?? null;
    this.addingLines = true;
    this.shipmentService.getOrderLines(poId).subscribe({
      next: (lines) => {
        this.poLines[poId] = lines.map((l) => ({ ...l }));
        const fresh = lines
          .filter(
            (l) =>
              !this.lines.some(
                (x) => x.containerKey === containerKey && x.purchaseOrderId === poId && x.productName === l.productName && x.sku === l.sku,
              ),
          )
          .map((l) => ({ ...l, containerKey }));
        this.lines.push(...fresh);
        this.clearPack();
        this.addingLines = false;
        this.orderToAdd = null;
        if (!fresh.length) this.alertService.alert(`That purchase order is already in ${this.containerLabel(containerKey)}`, 'error');
      },
      error: () => {
        this.addingLines = false;
        this.alertService.alert('Could not load that purchase order', 'error');
      },
    });
  }

  addBlankLine(containerKey: string | null = this.targetContainerKey ?? this.containers[0]?.key ?? null): void {
    this.clearPack();
    this.lines.push({
      containerKey,
      purchaseOrderId: null,
      productId: null,
      productName: '',
      sku: null,
      incoterm: null,
      quantity: 1,
      unitOfMeasure: 'PCS',
      currency: 'GBP',
      unitPrice: 0,
    });
  }

  // ---- Add from a purchase order (searchable) ----
  get filteredOrders(): ShipmentOrderSummary[] {
    const terms = this.orderSearch.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!terms.length) return this.orderChoices;
    return this.orderChoices.filter((o) => {
      const haystack = [o.reference, o.supplierName, o.status, o.currency, o.store].join(' ').toLowerCase();
      return terms.every((t) => haystack.includes(t));
    });
  }

  // How many lines of this PO are already on the shipment.
  linesForOrder(id: number): number {
    return this.lines.filter((l) => l.purchaseOrderId === id).length;
  }

  orderDisplay = (): string => '';

  onOrderPicked(order: ShipmentOrderSummary): void {
    this.orderSearch = '';
    this.orderToAdd = order.id;
    this.addOrderLines();
  }

  // Takes every line of one PO off the shipment.
  removeOrderLines(id: number): void {
    this.clearPack();
    this.lines = this.lines.filter((l) => l.purchaseOrderId !== id);
  }

  get orderGroups(): { id: number; reference: string; supplier: string | null; count: number }[] {
    const ids = [...new Set(this.lines.map((l) => l.purchaseOrderId).filter((id): id is number => !!id))];
    return ids.map((id) => {
      const po = this.orderChoices.find((o) => o.id === id);
      return { id, reference: this.orderReference(id), supplier: po?.supplierName ?? null, count: this.linesForOrder(id) };
    });
  }

  // A product picked on a hand-added line fills its id and SKU.
  onLineProductPicked(line: ShipmentLine, product: ProductPickerItem): void {
    line.productId = product.productId;
    line.productName = product.productName;
    line.sku = product.sku;
  }

  onLineProductEdited(line: ShipmentLine): void {
    line.productId = null;
  }

  // ---- Per-container state ----
  // New containers start where the shipment is (not cancelled ones).
  private get containerDefaultStatus(): string {
    return this.statusFlow.some((f) => f.value === this.status) ? this.status : 'BOOKED';
  }

  // The shipment stepper moves every container with it.
  setStatus(value: string): void {
    this.status = value;
    const index = this.statusFlow.findIndex((f) => f.value === value);
    if (index < 0) return;
    const clearedIndex = this.statusFlow.findIndex((f) => f.value === 'CLEARED');
    const today = new Date().toISOString().slice(0, 10);
    for (const c of this.containers) {
      // Received containers are done - their stock is booked.
      if (c.status === 'RECEIVED') continue;
      c.status = value;
      if (index >= clearedIndex) c.clearedAt = c.clearedAt || today;
      else c.clearedAt = null;
    }
  }

  // One container's state changed: stamp/clear its clearance date, then the
  // shipment follows its least advanced container (so it only reads
  // "Cleared" once every container has cleared).
  setContainerStatus(container: ShipmentContainer, value: string): void {
    if (container.status === 'RECEIVED' || value === 'RECEIVED') return;
    container.status = value;
    const index = this.statusFlow.findIndex((f) => f.value === value);
    const clearedIndex = this.statusFlow.findIndex((f) => f.value === 'CLEARED');
    if (index >= clearedIndex) container.clearedAt = container.clearedAt || new Date().toISOString().slice(0, 10);
    else container.clearedAt = null;
    if (this.isCancelled) return;
    const least = Math.min(...this.containers.map((c) => this.statusFlow.findIndex((f) => f.value === c.status)).filter((i) => i >= 0));
    if (Number.isFinite(least)) this.status = this.statusFlow[least].value;
  }

  containerStep(container: ShipmentContainer) {
    return this.statusFlow.find((f) => f.value === container.status) ?? this.statusFlow[0];
  }

  // "2 of 4 cleared" for the stepper strip.
  get clearedCount(): number {
    const clearedIndex = this.statusFlow.findIndex((f) => f.value === 'CLEARED');
    return this.containers.filter((c) => this.statusFlow.findIndex((f) => f.value === c.status) >= clearedIndex).length;
  }

  containerStatusClass(status: string): string {
    return shipmentStatusSoft(status);
  }

  // ---- Status ----
  get isCancelled(): boolean {
    return this.status === 'CANCELLED';
  }

  get statusIndex(): number {
    return this.statusFlow.findIndex((s) => s.value === this.status);
  }

  get statusLabel(): string {
    return this.statusFlow.find((s) => s.value === this.status)?.label ?? this.status.toLowerCase();
  }

  get voyageDone(): boolean {
    return ['ARRIVED', 'CLEARED', 'DELIVERED', 'RECEIVED'].includes(this.status);
  }

  // Under the voyage track: where the shipment is, in words.
  get voyageCaption(): string {
    if (this.isCancelled) return 'Shipment cancelled';
    if (this.dateError) return 'ETA is before the ETD';
    if (this.voyageDone) return this.status === 'ARRIVED' ? 'Arrived at port' : `${this.statusLabel}`;
    const d = this.daysToEta;
    if (!this.etd || !this.eta) return 'Add the ETD and ETA to track the voyage';
    if (d === null) return '';
    if (d < 0) return `${-d} day${d === -1 ? '' : 's'} past ETA`;
    if (d === 0) return 'Due in port today';
    return `${this.journeyPct ?? 0}% of the way · arrives in ${d} day${d === 1 ? '' : 's'}`;
  }

  get sailingDays(): number | null {
    if (!this.etd || !this.eta || this.dateError) return null;
    return Math.round((new Date(this.eta).getTime() - new Date(this.etd).getTime()) / 86_400_000);
  }

  get totalUnits(): number {
    return this.lines.reduce((n, l) => n + (Number(l.quantity) || 0), 0);
  }

  // What still stops the shipment being saved, shown in the save bar.
  get saveBlockers(): string[] {
    const blockers: string[] = [];
    const valid = this.lines.filter((l) => l.productName.trim());
    const noPo = valid.filter((l) => !l.purchaseOrderId).length;
    if (noPo) blockers.push(`${noPo} line${noPo === 1 ? '' : 's'} need${noPo === 1 ? 's' : ''} a PO`);
    if (valid.some((l) => !(Number(l.quantity) > 0))) blockers.push('Quantities must be above 0');
    if (this.dateError) blockers.push('ETA is before the ETD');
    const loose = this.unassignedLines.length;
    if (loose && this.containers.length) blockers.push(`${loose} line${loose === 1 ? '' : 's'} not in a container`);
    return blockers;
  }

  // Picking a PO on a line borrows its currency and incoterm, and loads its
  // lines so the product box offers them.
  onLineOrderChange(line: ShipmentLine): void {
    const po = this.orderChoices.find((o) => o.id === line.purchaseOrderId);
    if (po) {
      line.currency = po.currency || line.currency;
      line.incoterm = po.incoterms ?? line.incoterm;
      this.ensurePoLines(po.id);
    }
  }

  private ensurePoLines(poId: number): void {
    if (this.poLines[poId] || this.poLinesLoading.has(poId)) return;
    this.poLinesLoading.add(poId);
    this.shipmentService.getOrderLines(poId).subscribe({
      next: (lines) => {
        this.poLines[poId] = lines;
        this.poLinesLoading.delete(poId);
      },
      // Non-fatal: the line falls back to the catalogue product search.
      error: () => this.poLinesLoading.delete(poId),
    });
  }

  private sameProduct(a: ShipmentLine, b: ShipmentLine): boolean {
    return a.purchaseOrderId === b.purchaseOrderId && a.productName === b.productName && (a.sku ?? null) === (b.sku ?? null);
  }

  // The PO's lines matching what's typed in a line's product box.
  poLineOptions(line: ShipmentLine): ShipmentLine[] {
    const all = (line.purchaseOrderId && this.poLines[line.purchaseOrderId]) || [];
    const terms = (line.productId ? '' : line.productName).trim().toLowerCase().split(/\s+/).filter(Boolean);
    return terms.length
      ? all.filter((p) => terms.every((t) => `${p.productName} ${p.sku ?? ''}`.toLowerCase().includes(t)))
      : all;
  }

  // Outstanding on the PO minus what other lines of this shipment already carry.
  remainingFor(poLine: ShipmentLine, except?: ShipmentLine): number {
    const shipped = this.lines
      .filter((l) => l !== except && this.sameProduct(l, poLine))
      .reduce((n, l) => n + (Number(l.quantity) || 0), 0);
    return Math.max(0, Number(poLine.quantity) - shipped);
  }

  // Fills a line from the PO line picked: product, SKU, what's left to ship,
  // unit, currency, price and incoterm.
  pickPoLine(line: ShipmentLine, poLine: ShipmentLine): void {
    line.productId = poLine.productId;
    line.productName = poLine.productName;
    line.sku = poLine.sku;
    line.incoterm = poLine.incoterm;
    line.unitOfMeasure = poLine.unitOfMeasure;
    line.currency = poLine.currency;
    line.unitPrice = poLine.unitPrice;
    line.quantity = this.remainingFor(poLine, line);
  }

  poLineDisplay = (value: ShipmentLine | string | null): string =>
    typeof value === 'string' ? value : value?.productName ?? '';

  onPoLineTyped(line: ShipmentLine, text: string): void {
    line.productName = text;
    line.productId = null;
  }

  // Across every line of the shipment, more of this PO product than it has
  // outstanding - shown under the quantity.
  overShipped(line: ShipmentLine): { outstanding: number; total: number } | null {
    const poLine = line.purchaseOrderId ? this.poLines[line.purchaseOrderId]?.find((p) => this.sameProduct(p, line)) : undefined;
    if (!poLine) return null;
    const total = this.lines.filter((l) => this.sameProduct(l, line)).reduce((n, l) => n + (Number(l.quantity) || 0), 0);
    return total > Number(poLine.quantity) ? { outstanding: Number(poLine.quantity), total } : null;
  }

  removeLine(line: ShipmentLine): void {
    this.clearPack();
    this.lines = this.lines.filter((l) => l !== line);
  }

  // ---- Containers ----
  addContainer(): void {
    const key = 'n' + this.nextContainerKey++;
    this.containers.push({ key, containerNo: null, containerType: null, sealNo: null, status: this.containerDefaultStatus, clearedAt: null });
    this.targetContainerKey = key;
  }

  @ViewChild('poSearch') poSearch?: ElementRef<HTMLInputElement>;
  @ViewChild('poTrigger') poTrigger?: MatAutocompleteTrigger;

  // "Pick a PO into it" on an empty container: make it the target, then take
  // the user to the PO search with the list already open.
  pickPoInto(key: string): void {
    this.targetContainerKey = key;
    const input = this.poSearch?.nativeElement;
    if (!input) return;
    input.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => {
      input.focus({ preventScroll: true });
      this.poTrigger?.openPanel();
    }, 300);
  }

  // ---- Pack lines into containers by capacity ----
  // Usually 30 units fill a container; the user can pick 60 or any number.
  readonly packSizes = [30, 60];
  customPackSize: number | null = null;
  private packUndo: { lines: ShipmentLine[]; containers: ShipmentContainer[] } | null = null;
  lastPack: { size: number; summary: string } | null = null;

  // Fills containers in order, `size` units each: every product's units go
  // into the current container until it's full, then the next (adding new,
  // unnumbered containers when they run out). Products follow on from each
  // other, so a part-full container takes the next product. Lines of the
  // same PO product are merged first, so packing twice gives the same result.
  packIntoContainers(size: number | null): void {
    const capacity = Number(size);
    if (!(capacity > 0)) {
      this.alertService.alert('Enter how many units fit in one container', 'error');
      return;
    }
    const groups: { template: ShipmentLine; qty: number }[] = [];
    for (const line of this.lines) {
      const group = groups.find((g) => this.sameProduct(g.template, line) && g.template.unitPrice === line.unitPrice);
      if (group) group.qty += Number(line.quantity) || 0;
      else groups.push({ template: line, qty: Number(line.quantity) || 0 });
    }
    if (!groups.some((g) => g.qty > 0)) {
      this.alertService.alert('Add some lines with quantities first', 'error');
      return;
    }

    this.packUndo = {
      lines: this.lines.map((l) => ({ ...l })),
      containers: this.containers.map((c) => ({ ...c })),
    };
    const round = (n: number) => Math.round(n * 100) / 100;
    const packed: ShipmentLine[] = [];
    const fill: number[] = [];
    let ci = 0;
    let room = capacity;
    const containerAt = (i: number) => {
      while (this.containers.length <= i) this.addContainer();
      return this.containers[i].key;
    };
    for (const g of groups) {
      let left = round(g.qty);
      if (left <= 0) {
        packed.push({ ...g.template, id: undefined, containerKey: containerAt(ci), quantity: 0 });
        continue;
      }
      while (left > 0) {
        const take = round(Math.min(left, room));
        packed.push({ ...g.template, id: undefined, containerKey: containerAt(ci), quantity: take });
        fill[ci] = round((fill[ci] ?? 0) + take);
        left = round(left - take);
        room = round(room - take);
        if (room <= 0) {
          ci++;
          room = capacity;
        }
      }
    }
    this.lines = packed;
    // Containers left empty that were never numbered aren't needed.
    const used = new Set(packed.map((l) => l.containerKey));
    this.containers = this.containers.filter((c) => used.has(c.key) || c.containerNo?.trim());
    this.targetContainerKey = this.containers[0]?.key ?? null;
    const counts = fill.filter((n) => n > 0);
    this.lastPack = { size: capacity, summary: `${counts.length} container${counts.length === 1 ? '' : 's'} · ${counts.join(' / ')}` };
  }

  // Any other change to the lines makes the undo stale - drop it.
  private clearPack(): void {
    this.packUndo = null;
    this.lastPack = null;
  }

  undoPack(): void {
    if (!this.packUndo) return;
    this.lines = this.packUndo.lines;
    this.containers = this.packUndo.containers;
    this.targetContainerKey = this.containers[0]?.key ?? null;
    this.packUndo = null;
    this.lastPack = null;
  }

  // ---- Several containers at once ----
  showPasteContainers = false;
  pastedContainers = '';

  // Container numbers in what was pasted (commas, spaces or new lines),
  // upper-cased, without repeats or ones already on the shipment.
  get pastedContainerNos(): string[] {
    const existing = new Set(this.containers.map((c) => (c.containerNo || '').trim().toUpperCase()).filter(Boolean));
    return this.pastedParts.filter((v) => !existing.has(v));
  }

  // Pasted numbers that are already on the shipment - shown, not added again.
  get pastedAlreadyAdded(): string[] {
    const existing = new Set(this.containers.map((c) => (c.containerNo || '').trim().toUpperCase()).filter(Boolean));
    return this.pastedParts.filter((v) => existing.has(v));
  }

  private get pastedParts(): string[] {
    const parts = this.pastedContainers
      .split(/[\s,;]+/)
      .map((v) => v.trim().toUpperCase())
      .filter(Boolean);
    return parts.filter((v, i) => parts.indexOf(v) === i);
  }

  // Fills containers that have no number yet first, then adds the rest -
  // so pasting the list into a new shipment doesn't leave a blank one.
  addPastedContainers(): void {
    const numbers = this.pastedContainerNos;
    if (!numbers.length) return;
    const blanks = this.containers.filter((c) => !c.containerNo?.trim());
    for (const no of numbers) {
      const blank = blanks.shift();
      if (blank) {
        blank.containerNo = no;
      } else {
        this.addContainer();
        this.containers[this.containers.length - 1].containerNo = no;
      }
    }
    this.alertService.alert(`${numbers.length} container${numbers.length === 1 ? '' : 's'} added`, 'success');
    this.pastedContainers = '';
    this.showPasteContainers = false;
  }

  // A list typed or pasted into one container's number box ("A,B,C"):
  // the first stays on this container and each of the rest becomes a new
  // container right after it. Repeats and numbers already on the shipment
  // are skipped.
  splitContainerNo(container: ShipmentContainer): void {
    const parts = (container.containerNo || '')
      .split(/[\s,;]+/)
      .map((v) => v.trim().toUpperCase())
      .filter(Boolean);
    if (parts.length < 2) return;
    const taken = new Set(
      this.containers.filter((c) => c !== container).map((c) => (c.containerNo || '').trim().toUpperCase()).filter(Boolean),
    );
    const unique = parts.filter((v, i) => parts.indexOf(v) === i && !taken.has(v));
    container.containerNo = unique[0] ?? parts[0];
    const extra = unique.slice(1).map((no) => ({
      key: 'n' + this.nextContainerKey++,
      containerNo: no,
      containerType: null,
      sealNo: null,
      status: container.status,
      clearedAt: container.clearedAt,
    }));
    this.containers.splice(this.containers.indexOf(container) + 1, 0, ...extra);
    if (extra.length) {
      const hasLines = this.linesIn(container.key).length > 0;
      this.alertService.alert(
        `${extra.length} more container${extra.length === 1 ? '' : 's'} added` +
          (hasLines ? ' - use ⋯ › Spread across all containers to share the lines' : ''),
        'success',
      );
    }
  }

  // Paste lands in the model after the event - split once it has.
  pasteContainerNo(container: ShipmentContainer): void {
    setTimeout(() => this.splitContainerNo(container));
  }

  // Spreads a line evenly over every container - one line per container,
  // whole units, any remainder going to the first ones (116 over 4 = 29
  // each). The quantities can then be adjusted where the split differs.
  spreadLine(line: ShipmentLine): void {
    this.clearPack();
    const keys = this.containers.map((c) => c.key);
    if (keys.length < 2) return;
    const total = Number(line.quantity) || 0;
    const whole = Number.isInteger(total);
    // Work in hundredths for fractional quantities (m2 etc.).
    const units = whole ? total : Math.round(total * 100);
    const base = Math.floor(units / keys.length);
    let remainder = units - base * keys.length;
    const qtyFor = () => {
      const q = base + (remainder > 0 ? 1 : 0);
      remainder--;
      return whole ? q : q / 100;
    };
    // The line itself becomes the first container's share.
    const index = this.lines.indexOf(line);
    const copies: ShipmentLine[] = keys.map((key, i) =>
      i === 0 ? Object.assign(line, { containerKey: key, quantity: qtyFor() }) : { ...line, id: undefined, containerKey: key, quantity: qtyFor() },
    );
    this.lines.splice(index, 1, ...copies);
    this.alertService.alert(`Spread over ${keys.length} containers - adjust any that differ`, 'success');
  }

  // Lines in a removed container move to the first one left, so nothing is
  // lost by tidying containers up.
  removeContainer(container: ShipmentContainer): void {
    const rest = this.containers.filter((c) => c !== container);
    if (!rest.length) return;
    const moved = this.lines.filter((l) => l.containerKey === container.key);
    moved.forEach((l) => (l.containerKey = rest[0].key));
    this.containers = rest;
    if (this.targetContainerKey === container.key) this.targetContainerKey = rest[0].key;
    if (moved.length) {
      this.alertService.alert(`${moved.length} line${moved.length === 1 ? '' : 's'} moved to ${this.containerLabel(rest[0].key)}`, 'success');
    }
  }

  containerLabel(key: string | null | undefined): string {
    const index = this.containers.findIndex((c) => c.key === key);
    if (index < 0) return 'no container';
    const c = this.containers[index];
    return c.containerNo?.trim() ? c.containerNo.trim().toUpperCase() : `Container ${index + 1}`;
  }

  // Container numbers are 4 letters + 7 digits (ISO 6346, e.g. MSKU1234567)
  // - only a hint, since some carriers' paperwork differs.
  containerNoLooksOff(c: ShipmentContainer): boolean {
    const v = (c.containerNo || '').replace(/\s+/g, '').toUpperCase();
    return !!v && !/^[A-Z]{4}\d{7}$/.test(v);
  }

  moveLine(line: ShipmentLine, key: string): void {
    this.clearPack();
    line.containerKey = key;
  }

  // Splits a line in two - half the quantity stays, half goes into the
  // chosen container (often the same product across two boxes).
  splitLine(line: ShipmentLine, key: string): void {
    this.clearPack();
    const qty = Number(line.quantity) || 0;
    const keep = Math.ceil(qty / 2);
    const copy: ShipmentLine = { ...line, id: undefined, containerKey: key, quantity: Math.max(0, qty - keep) };
    line.quantity = keep;
    const index = this.lines.indexOf(line);
    this.lines.splice(index + 1, 0, copy);
  }

  linesIn(key: string | null): ShipmentLine[] {
    return this.lines.filter((l) => (l.containerKey ?? null) === key);
  }

  // Lines whose container isn't on the shipment (only from old data).
  get unassignedLines(): ShipmentLine[] {
    const keys = new Set(this.containers.map((c) => c.key));
    return this.lines.filter((l) => !l.containerKey || !keys.has(l.containerKey));
  }

  unitsIn(lines: ShipmentLine[]): number {
    return lines.reduce((n, l) => n + (Number(l.quantity) || 0), 0);
  }

  totalsFor(lines: ShipmentLine[]): { currency: string; total: number }[] {
    const totals = new Map<string, number>();
    for (const line of lines) totals.set(line.currency, (totals.get(line.currency) ?? 0) + this.lineSubtotal(line));
    return Array.from(totals, ([currency, total]) => ({ currency, total }));
  }

  lineSubtotal(line: ShipmentLine): number {
    return (Number(line.quantity) || 0) * (Number(line.unitPrice) || 0);
  }

  // Totals per currency - a shipment can carry POs in different currencies.
  get totalsByCurrency(): { currency: string; total: number }[] {
    const totals = new Map<string, number>();
    for (const line of this.lines) {
      totals.set(line.currency, (totals.get(line.currency) ?? 0) + this.lineSubtotal(line));
    }
    return Array.from(totals, ([currency, total]) => ({ currency, total }));
  }

  // Purchase orders currently on the shipment's lines.
  get poReferences(): string[] {
    return [...new Set(this.lines.filter((l) => l.purchaseOrderId).map((l) => this.orderReference(l.purchaseOrderId)))];
  }

  get dateError(): boolean {
    return !!(this.etd && this.eta && this.eta < this.etd);
  }

  // Whole days from today to the ETA (negative = past it). Only while the
  // shipment is still on its way to port.
  get daysToEta(): number | null {
    if (!this.eta || !['BOOKED', 'IN TRANSIT'].includes(this.status)) return null;
    const [y, m, d] = this.eta.split('-').map(Number);
    const today = new Date();
    return Math.round((Date.UTC(y, m - 1, d) - Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())) / 86_400_000);
  }

  // How far through its sailing the shipment is (ETD -> ETA).
  get journeyPct(): number | null {
    if (!this.etd || !this.eta || this.dateError) return null;
    if (['ARRIVED', 'CLEARED', 'DELIVERED', 'RECEIVED'].includes(this.status)) return 100;
    if (this.status === 'CANCELLED') return null;
    const start = new Date(this.etd).getTime();
    const end = new Date(this.eta).getTime();
    if (end <= start) return null;
    return Math.max(0, Math.min(100, Math.round(((Date.now() - start) / (end - start)) * 100)));
  }

  onSave(): void {
    if (this.etd && this.eta && this.eta < this.etd) {
      this.alertService.alert('ETA is before the ETD', 'error');
      return;
    }
    const validLines = this.lines.filter((l) => l.productName.trim());
    if (validLines.some((l) => !l.purchaseOrderId)) {
      this.alertService.alert('Choose a purchase order for every line', 'error');
      return;
    }
    if (validLines.some((l) => !(Number(l.quantity) > 0))) {
      this.alertService.alert('Every line needs a quantity above zero', 'error');
      return;
    }
    if (this.containers.length && this.unassignedLines.some((l) => l.productName.trim())) {
      this.alertService.alert('Put every line in a container', 'error');
      return;
    }
    const input = {
      invoiceNumber: this.invoiceNumber || null,
      invoiceDate: this.invoiceDate || null,
      billOfLading: this.billOfLading || null,
      etd: this.etd || null,
      eta: this.eta || null,
      portOfLoading: this.portOfLoading || null,
      portOfDischarge: this.portOfDischarge || null,
      vesselDetails: this.vesselDetails || null,
      shippingLine: this.shippingLine || null,
      store: this.store,
      status: this.status,
      notes: this.notes || null,
      containers: this.containers.map((c) => ({
        id: c.id ?? null,
        key: c.key,
        containerNo: c.containerNo?.trim() || null,
        containerType: c.containerType || null,
        sealNo: c.sealNo?.trim() || null,
        status: c.status || null,
        clearedAt: c.clearedAt || null,
      })),
      items: validLines,
    };
    this.saving = true;
    const request =
      this.isEditMode && this.shipmentId
        ? this.shipmentService.updateShipment(this.shipmentId, input)
        : this.shipmentService.createShipment(input);
    request.subscribe({
      next: () => {
        this.saving = false;
        this.alertService.alert(this.isEditMode ? 'Shipment updated' : 'Shipment created', 'success');
        this.backToList();
      },
      error: (error) => {
        this.saving = false;
        this.alertService.alert(error?.error?.message || 'Could not save shipment', 'error');
      },
    });
  }

  onCancel(): void {
    this.backToList();
  }

  // The list's last filters/page, for the breadcrumb and backToList.
  readonly listQuery = lastListQuery(SHIPMENTS_LIST_KEY);

  // Back to the shipments list on the same filters/page it was left on.
  private backToList(): void {
    this.router.navigate(['/shipments'], { queryParams: lastListQuery(SHIPMENTS_LIST_KEY) });
  }
}
